import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from '@/components/ui/sheet';

const Navigation = () => {
  const location = useLocation();
  
  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'AGOS', label: 'AGOS Member', path: '/agos' },
    { name: 'Program', path: '/program', highlight: true },
    { name: 'Faculty', path: '/faculty' },
    { name: 'Abstracts', label: 'Paper/Poster', path: '/abstracts' },
    { name: 'Travel', path: '/travel' },
    { name: 'Accommodation', path: '/accommodation' },
    { name: 'Contact', path: '/contact' },
    { name: 'My Registration', path: '/my-registration' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass-card border-b border-white/10" data-testid="main-navigation">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <div className="text-2xl md:text-3xl font-bold">
              <span className="gradient-text">AGOC</span>
              <span className="text-white ml-2">2026</span>
            </div>
          </Link>

          <div className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => (
              <Link key={item.path} to={item.path}>
                <Button
                  variant="ghost"
                  className={`${
                    isActive(item.path)
                      ? 'text-[#FF6F4A] bg-white/10'
                      : item.highlight
                        ? 'text-[#FF6F4A] font-bold nav-blink ring-1 ring-[#FF6F4A]/50'
                        : 'text-white hover:text-[#FF6F4A]'
                  } transition-all duration-300`}
                  data-testid={`nav-${item.name.toLowerCase()}`}
                >
                  {item.label || item.name}
                  {item.highlight && (
                    <span className="ml-1.5 w-2 h-2 rounded-full bg-[#FF6F4A] inline-block animate-ping" />
                  )}
                </Button>
              </Link>
            ))}
          </div>

          <div className="hidden lg:block">
            <Link to="/registration">
              <Button
                className="bg-[#FF6F4A] hover:bg-[#FF6F4A]/90 text-white font-semibold px-6 neon-glow"
                data-testid="register-button-nav"
              >
                Register Now
              </Button>
            </Link>
          </div>

          <Sheet>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon" data-testid="mobile-menu-button">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent className="bg-[#1C0A4D] border-white/10">
              <div className="flex flex-col gap-4 mt-8">
                {navItems.map((item) => (
                  <Link key={item.path} to={item.path}>
                    <Button
                      variant="ghost"
                      className={`w-full justify-start ${
                        isActive(item.path)
                          ? 'text-[#FF6F4A] bg-white/10'
                          : item.highlight
                            ? 'text-[#FF6F4A] font-bold nav-blink'
                            : 'text-white hover:text-[#FF6F4A]'
                      }`}
                      data-testid={`mobile-nav-${item.name.toLowerCase()}`}
                    >
                      {item.label || item.name}
                      {item.highlight && (
                        <span className="ml-1.5 w-2 h-2 rounded-full bg-[#FF6F4A] inline-block animate-ping" />
                      )}
                    </Button>
                  </Link>
                ))}
                <Link to="/registration" className="mt-4">
                  <Button
                    className="w-full bg-[#FF6F4A] hover:bg-[#FF6F4A]/90 text-white font-semibold"
                    data-testid="register-button-mobile"
                  >
                    Register Now
                  </Button>
                </Link>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;